export const BLOCKDAG = {
  chainId: 1404,
  chainIdHex: "0x57c",
  name: "BlockDAG Mainnet",
  symbol: "BDAG",
  rpcUrl: "https://rpc.blockdag.works/",
  rpcUrls: [
    "https://rpc.blockdag.works/",
    "https://rpc.bdagscan.com/",
  ],
  explorerUrl: "https://bdagscan.com",
} as const;

type RpcEnvelope<T> = {
  jsonrpc?: string;
  id?: number;
  result?: T;
  error?: { code?: number; message?: string };
};

export function isAddress(value: string): boolean {
  return /^0x[0-9a-fA-F]{40}$/.test(value.trim());
}

export function shortenAddress(value: string, left = 6, right = 4): string {
  if (!isAddress(value)) return value;
  return `${value.slice(0, left)}…${value.slice(-right)}`;
}

export function formatBdag(hexWei: string, precision = 4): string {
  const wei = BigInt(hexWei);
  const unit = 10n ** 18n;
  const whole = wei / unit;
  const fraction = (wei % unit).toString().padStart(18, "0").slice(0, precision);
  return `${whole.toLocaleString("en-GB")}.${fraction}`;
}

export function hexToNumber(hex: string): number {
  const parsed = Number.parseInt(hex, 16);
  if (!Number.isSafeInteger(parsed)) throw new Error("Network value is outside the safe range.");
  return parsed;
}

export async function rpc<T>(
  method: string,
  params: unknown[] = [],
  signal?: AbortSignal,
): Promise<T> {
  let lastError: unknown;
  for (const rpcUrl of BLOCKDAG.rpcUrls) {
    if (signal?.aborted) throw new DOMException("The request was aborted.", "AbortError");
    try {
      const response = await fetch(rpcUrl, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ jsonrpc: "2.0", id: 1, method, params }),
        signal,
      });

      if (!response.ok) throw new Error(`RPC returned HTTP ${response.status}.`);
      const body = (await response.json()) as RpcEnvelope<T>;
      if (body.error) throw new Error(body.error.message || "BlockDAG RPC rejected the request.");
      if (body.result === undefined) throw new Error("BlockDAG RPC returned an incomplete response.");
      return body.result;
    } catch (caught) {
      lastError = caught;
    }
  }
  throw lastError instanceof Error ? lastError : new Error("All BlockDAG RPC endpoints are unavailable.");
}

export async function readPortfolio(address: string, signal?: AbortSignal) {
  if (!isAddress(address)) throw new Error("Enter a valid 0x BlockDAG address.");
  const [chainHex, balanceHex, blockHex, nonceHex] = await Promise.all([
    rpc<string>("eth_chainId", [], signal),
    rpc<string>("eth_getBalance", [address, "latest"], signal),
    rpc<string>("eth_blockNumber", [], signal),
    rpc<string>("eth_getTransactionCount", [address, "latest"], signal),
  ]);

  const chainId = hexToNumber(chainHex);
  if (chainId !== BLOCKDAG.chainId) {
    throw new Error(`Safety check failed: RPC reported chain ${chainId}, expected ${BLOCKDAG.chainId}.`);
  }

  return {
    balance: formatBdag(balanceHex),
    block: hexToNumber(blockHex),
    nonce: hexToNumber(nonceHex),
  };
}
